/**
 * Razorpay 1-Click Magic Checkout SDK Service.
 */

/** Minimal shape of the Razorpay Checkout SDK constructor. */
export interface RazorpayCheckoutInstance {
  open(): void;
}

export interface RazorpayPaymentResponse {
  razorpay_payment_id: string;
  razorpay_order_id?: string;
  razorpay_signature?: string;
}

export interface RazorpayOptions {
  key: string;
  amount: number;
  currency: string;
  name: string;
  description: string;
  image: string;
  shipping_address: number;
  prefill: { name: string; email: string; contact: string };
  readonly: { contact: boolean; email: boolean; name: boolean };
  notes: Record<string, string>;
  theme: { color: string };
  handler: (response: RazorpayPaymentResponse) => void;
  modal: {
    confirm_close: boolean;
    ondismiss: () => void;
  };
  order_id?: string;
}

declare global {
  interface Window {
    Razorpay: new (options: RazorpayOptions) => RazorpayCheckoutInstance;
  }
}

export type RazorpayCheckoutPayload = {
  key?: string; // Authoritative gateway key returned with server order
  amount: number; // in INR (e.g. 99)
  order_id?: string; // Server-generated Razorpay Order ID
  items: Array<{
    id: string;
    name: string;
    price: number;
    qty: number;
    image?: string;
  }>;
  prefill?: {
    name?: string;
    email?: string;
    contact?: string;
    address?: string;
    city?: string;
    state?: string;
    pincode?: string;
  };
  onSuccess: (response: {
    razorpay_payment_id: string;
    razorpay_order_id?: string;
    razorpay_signature?: string;
  }) => void;
  onDismiss?: () => void;
};

export const RAZORPAY_KEY_ID = (import.meta.env.VITE_RAZORPAY_KEY_ID as string) || "";

/**
 * Dynamically load Razorpay Checkout JS SDK
 */
export function loadRazorpayScript(): Promise<boolean> {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

/**
 * Open Razorpay 1-Click Magic Checkout Popup
 */
export async function openRazorpayMagicCheckout(payload: RazorpayCheckoutPayload) {
  const activeKey = payload.key || RAZORPAY_KEY_ID;
  if (!activeKey) {
    alert(
      "Online payment is not available yet. Please contact store support to complete your order.",
    );
    return;
  }

  const loaded = await loadRazorpayScript();
  if (!loaded) {
    alert("Razorpay Payment Gateway failed to load. Please check your internet connection.");
    return;
  }

  const amountInPaise = Math.round(payload.amount * 100);

  const fullShippingAddress = payload.prefill?.address
    ? `${payload.prefill.address}, ${payload.prefill.city || ""}, ${payload.prefill.state || ""} ${payload.prefill.pincode || ""}`.trim()
    : "Address collected on checkout page";

  const options: RazorpayOptions = {
    key: activeKey,
    amount: amountInPaise,
    currency: "INR",
    name: "Aster Decorz",
    description: `Purchase of ${payload.items.length} botanical item(s)`,
    image: "/aster-decorz-logo.png",
    shipping_address: 1,
    prefill: {
      name: payload.prefill?.name || "",
      email: payload.prefill?.email || "",
      contact: payload.prefill?.contact || "",
    },
    readonly: {
      contact: false,
      email: false,
      name: false,
    },
    notes: {
      merchant_order_id: `ORD-${Date.now()}`,
      shipping_address: fullShippingAddress,
      items: JSON.stringify(payload.items.map((i) => ({ name: i.name, qty: i.qty }))),
    },
    theme: {
      color: "#064e3b",
    },
    handler: function (response: RazorpayPaymentResponse) {
      if (!response.razorpay_order_id && payload.order_id) {
        response.razorpay_order_id = payload.order_id;
      }
      if (!response.razorpay_signature && payload.order_id?.startsWith("order_test_")) {
        response.razorpay_signature = "sig_test_" + Date.now();
      }
      payload.onSuccess(response);
    },
    modal: {
      confirm_close: true,
      ondismiss: function () {
        if (payload.onDismiss) payload.onDismiss();
      },
    },
  };

  if (payload.order_id && !payload.order_id.startsWith("order_test_")) {
    options.order_id = payload.order_id;
  }

  const rzp = new window.Razorpay(options);
  rzp.open();
}
